// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL-3.0 section 7(b) apply — see ADDITIONAL-TERMS.md.

// Click-to-define tooltips for the technical terms in the UI.

export const GLOSSARY: Record<string, string> = {
  rnnoise:
    'A small recurrent neural network trained to separate a human voice from background noise, frame by frame. It runs here as a WebAssembly module, entirely on your device.',
  vad: 'Voice-activity detection — RNNoise scores each 10 ms frame for how likely it contains speech. We use it to measure the noise floor in the gaps between words.',
  'noise floor':
    'The level of the constant background hiss/hum/rumble underneath the voice, measured in dBFS. Lower (more negative) is quieter.',
  dbfs: 'Decibels relative to full scale: 0 dBFS is the loudest a digital signal can be; quiet backgrounds sit around -50 to -70 dBFS.',
  'sample rate':
    'How many audio samples per second. RNNoise works at 48,000 Hz, so we resample your recording to that rate before cleaning it.',
  wasm: 'WebAssembly — a fast, sandboxed binary format that lets the noise-suppression model run at near-native speed inside the browser tab.',
  'dry / wet':
    'Dry is your original recording; wet is the fully denoised version. The strength slider blends between them so you can dial back if it sounds over-processed.',
};

let tooltipEl: HTMLElement | null = null;

function ensureTooltip(): HTMLElement {
  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.className = 'glossary-tooltip';
    tooltipEl.hidden = true;
    document.body.appendChild(tooltipEl);
  }
  return tooltipEl;
}

function hide(): void {
  if (tooltipEl) tooltipEl.hidden = true;
}

/** Wire up all `.glossary-link[data-term]` spans within `root`. */
export function initGlossary(root: HTMLElement): void {
  const tip = ensureTooltip();

  root.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest('.glossary-link') as HTMLElement | null;
    if (!target) return;
    e.preventDefault();
    const term = (target.dataset.term || target.textContent || '').toLowerCase().trim();
    const def = GLOSSARY[term];
    if (!def) return;
    tip.textContent = def;
    tip.hidden = false;
    const rect = target.getBoundingClientRect();
    const tw = Math.min(280, window.innerWidth - 24);
    tip.style.width = `${tw}px`;
    let left = rect.left + rect.width / 2 - tw / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - tw - 12));
    tip.style.left = `${left}px`;
    tip.style.top = `${rect.bottom + 8 + window.scrollY}px`;
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hide();
  });
  document.addEventListener('click', (e) => {
    if (!(e.target as HTMLElement).closest('.glossary-link') &&
        !(e.target as HTMLElement).closest('.glossary-tooltip')) {
      hide();
    }
  });
  window.addEventListener('scroll', hide, { passive: true });
  window.addEventListener('resize', hide);
}
